/**
 * Shapes every `/v1/merchant` endpoint speaks, kept in one place.
 *
 * The API is bilingual and paged throughout: a display string is a `Translation`
 * rather than a string, and a list answers with `{ count, rows }` rather than a
 * bare array. Modules used to restate both for themselves, which is how two
 * features end up disagreeing about whether `rows` can be missing.
 */

/**
 * A display string, keyed by language. Both halves are optional on the way out —
 * a record created before Khmer was filled in still has to render.
 */
export interface Translation {
  en: string | null
  km: string | null
}

/** The paged envelope every list endpoint on this API returns. */
export interface PageResult<T> {
  count: number
  rows: T[]
}

/** An uploaded file as the API hands it back. */
export interface FileRef {
  id: string
  url: string
  name: string
  type: string
}

/** What every list endpoint accepts. `limit` is capped at 100 server-side. */
export interface PagingQuery {
  limit: number
  offset: number
}

/**
 * The outlet filter as query params: `{ branchIds }`, or nothing at all.
 *
 * The one place the app decides what an empty selection means, and it means
 * EVERY outlet — so the key is left off rather than sent empty. The DTOs read an
 * absent `branchIds` as the caller's whole scope, and the alternative readings
 * are both wrong in ways nobody would catch: `branchIds=` is a 400, and a
 * server that treated `[]` as "no outlets" would answer an empty page to a
 * merchant who had just cleared the filter.
 *
 * Spread into a query rather than assigned, so "unfiltered" is a missing key and
 * not an explicit `undefined` travelling through a React Query key.
 */
export function branchParam(branchIds: readonly string[]): { branchIds?: string[] } {
  return branchIds.length ? { branchIds: [...branchIds] } : {}
}

/** The display name for a translated string, preferring English and never blank. */
export function translated(value: Translation | null | undefined, fallback = 'Untitled'): string {
  return value?.en?.trim() || value?.km?.trim() || fallback
}

/** A blank pair, for a form that is creating rather than editing. */
export function emptyTranslation(): Translation {
  return { en: '', km: '' }
}

/**
 * A `Translation` on the way up.
 *
 * The API stores exactly what it is sent, so a blank Khmer field has to travel as
 * `null` rather than `''` — otherwise a record that never had a Khmer name reads
 * as one whose Khmer name is the empty string, and `translated()` can no longer
 * fall back.
 */
export function toTranslation(en: string, km?: string): Translation {
  return { en: en.trim() || null, km: km?.trim() || null }
}

/**
 * Fetches a whole collection through a paged endpoint.
 *
 * Pickers need the entire list, not a page of it. The API caps a page at 100, so
 * anything longer takes a second call rather than silently arriving truncated.
 */
export const PAGE_LIMIT_MAX = 100

/** A list page's row range, for the `1–10 of 42` line under a table. */
export function pageRange(page: number, size: number, total: number) {
  const first = total === 0 ? 0 : page * size + 1
  const last = Math.min(total, (page + 1) * size)

  return { first, last, hasNext: last < total }
}
